import { createAction } from 'typesafe-actions';
import { takeLatest, put, call } from 'redux-saga/effects';
import { PDP_REVIEWS_ADD_NEW_CLICK } from 'features/product-reviews';
import { setAuthorDetailTimeUsage, setTotalScore } from 'features/review-form/state';
import log from 'loglevel';
import { CallEffectReturnType } from 'types/utils';
import { PHOTO_SELECTION_CLICK, AUTHOR_REGION_SELECTED, AUTHOR_SHOP_SELECTED } from 'features/review-form';
import { REVIEWS_GROUP_SET } from 'features/reviews';
import { GROUP_OF_VARIANTS } from 'common/constants';
import { EReviewsElementBlock } from './types';

type Score = '1' | '2' | '3' | '4' | '5';
type DurationOfUseIndex = '1' | '2' | '3' | '4';

const ACTION_NAME = 'GA:clickButton';

/**
 * Событие должно срабатывать при клике на кнопку Написать отзыв
 *
 * @see https://docs.google.com/document/d/1vAT_kS09tM_69q5YshNF9o88nh3O1n17Tmt2nKdd8gw/edit#heading=h.uv70gc68awpo
 */
export const addNewReview = createAction(
  ACTION_NAME,
  () =>
    ({
      elementType: 'button',
      elementBlock: EReviewsElementBlock.PAGE_SECTION, // блок, в котором расположен элемент

      elementName: 'WriteReview', // условное обозначение кнопки
      elementText: 'Написать отзыв', // текст на кнопке
    }) as const,
)();

/**
 * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.6iyxi97hhg11
 */
export const rate = createAction(
  ACTION_NAME,
  ({ elementName, elementText }: { elementName: Score; elementText: string }) => ({
    elementBlock: EReviewsElementBlock.FORM,
    elementName,
    elementText,
  }),
)();

/**
 * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.o2qkhh56xye1
 */
const selectPhoto = createAction(ACTION_NAME, () => ({
  elementType: 'button',
  elementBlock: EReviewsElementBlock.FORM,
  elementName: 'photo',
  elementText: 'Загрузка фото',
}))();

/**
 * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.sfb1lvrrqpw
 */
const setDurationOfUse = createAction(
  ACTION_NAME,
  ({ elementName }: { elementName: DurationOfUseIndex }) => ({
    elementType: 'button',
    elementBlock: EReviewsElementBlock.FORM,
    elementName,
    elementText: 'Срок использования',
  }),
)();

/**
 * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.epkse0p5nz6q
 */
export const setRegion = createAction(ACTION_NAME, ({ elementName }: { elementName: string }) => ({
  elementType: 'button',
  elementBlock: EReviewsElementBlock.FORM,
  elementName,
  elementText: 'Место покупки - город',
}))();

/**
 * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.epkse0p5nz6q
 */
export const setShop = createAction(ACTION_NAME, ({ elementName }: { elementName: string }) => ({
  elementType: 'button',
  elementBlock: EReviewsElementBlock.FORM,
  elementName,
  elementText: 'Место покупки - магазин',
}))();

/**
 * Событие должно срабатывать при клике на вкладку «Все отзывы» / «Выбранный вариант».
 */
export const selectReviewsTab = createAction(
  ACTION_NAME,
  ({ elementText }: { elementText: 'Все отзывы' | 'Выбранный вариант' }) =>
    ({
      elementType: 'button',
      elementBlock: EReviewsElementBlock.PAGE_SECTION,
      elementName: 'reviewsTab',
      elementText,
    }) as const,
)();

export function* runGaClickButtonDetector() {
  yield takeLatest(PDP_REVIEWS_ADD_NEW_CLICK, function* putGaClickButton() {
    yield put(addNewReview());
  });

  yield takeLatest(setTotalScore, function* putGaClickButton({ payload }) {
    yield call(putRateActionIfValid, { score: payload, elementText: 'Общая оценка' });
  });

  yield takeLatest(PHOTO_SELECTION_CLICK, function* putGaClickButton() {
    yield put(selectPhoto());
  });

  yield takeLatest(setAuthorDetailTimeUsage, function* putGaClickButton({ payload }) {
    yield put(
      setDurationOfUse({
        elementName: String(payload.indexFromOne) as DurationOfUseIndex,
      }),
    );
  });

  yield takeLatest(AUTHOR_REGION_SELECTED, function* putGaClickButton({ payload }) {
    yield put(setRegion({ elementName: payload.regionName }));
  });

  yield takeLatest(AUTHOR_SHOP_SELECTED, function* putGaClickButton({ payload }) {
    yield put(setShop({ elementName: payload.shopName }));
  });

  yield takeLatest(REVIEWS_GROUP_SET, function* putGaClickButton({ payload }) {
    yield put(
      selectReviewsTab({
        elementText: payload === GROUP_OF_VARIANTS ? 'Все отзывы' : 'Выбранный вариант',
      }),
    );
  });

  function* putRateActionIfValid({ score, elementText }: { score: number; elementText: string }) {
    const intScore = (yield call(toInt, score)) as CallEffectReturnType<typeof toInt>;

    if (isIntScoreValid(intScore)) {
      yield put(
        rate({
          elementName: String(intScore) as Score,
          elementText,
        }),
      );
    } else {
      yield call([log, 'warn'], `Invalid score for '${elementText}' element`);
    }
  }

  function toInt(value: number) {
    return Math.round(value);
  }

  function isIntScoreValid(score: number) {
    return score >= 1 && score <= 5;
  }
}
